<template>
  <div id="app" :class="{ neositek: isNeositek }">
    <div v-if="otherWindow && blockMultiWindow">
      <div class="testing-global-message">
        <div class="message">
          <strong>Ya tienes la aplicación abierta en otra pestaña</strong><br />
          Solo puede haber una pestaña con la aplicación abierta.<br />
          Si quieres continuar en esta pestaña cierra la anterior y actualiza.
        </div>
      </div>
    </div>
    <template v-else>
      <transition name="fade">
        <div v-if="!loaded" id="loader" :class="{ update: isUpdate() }">
          <img id="logo" alt="app logo" :src="lightLogo" />
          <div class="spinner"></div>
          <div class="text">{{ loadingText }}</div>
          <div v-if="isUpdate()" class="progress-container">
            <el-progress
              :percentage="updatePercentage"
              :status="updatePercentage >= 100 ? 'success' : 'warning'"
              :stroke-width="22"
              :text-inside="true"
            />
          </div>
        </div>
      </transition>
      <router-view />
    </template>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { SANTANDER_WG, JOVELLANOS_WG, AVILES_WG } from "@/utils/constants";
import flavours from "@/mixins/flavours";
import { getStreetsForStreetInput } from "./api/operations/streets";
const WAIT_TIME_UPDATE = 8000;
const WAIT_TIME_LOGGED_IN = 3000;
const WAIT_TIME = 800;
const THIRTY_MINUTES = 1800000;

export default {
  name: "App",
  mixins: [flavours],
  data() {
    return {
      loaded: false,
      updatePercentage: 0,
      updateTimer: null,
      checkOperatorTimer: null,
      otherWindow: false,
      reloadDataInterval: null,
    };
  },
  beforeDestroy() {
    window.removeEventListener("storage", this.localStorageEventListener);
    if (this.updateTimer) {
      clearInterval(this.updateTimes);
    }
    if (this.checkOperatorTimer) {
      clearInterval(this.checkOperatorTimer);
    }
    if (this.reloadDataInterval) {
      clearInterval(this.reloadDataInterval);
    }
  },
  async created() {
    this.checkOperator();
    this.checkOperatorTimer = setInterval(this.checkOperator, 60000);
    if (this.isUpdate()) {
      this.updateTimer = setInterval(() => {
        this.updatePercentage = Math.round(
          Math.min(100, this.updatePercentage + 100 / ((WAIT_TIME_UPDATE - 1000) / 1000) / 2)
        );
        if (this.updatePercentage >= 100) {
          clearInterval(this.updateTimer);
        }
      }, 500);
    }

    if (this.token) {
      setTimeout(
        () => {
          this.loaded = true;
        },
        this.isUpdate() ? WAIT_TIME_UPDATE : WAIT_TIME_LOGGED_IN
      );
    } else {
      setTimeout(() => {
        this.loaded = true;
      }, WAIT_TIME);
    }

    document.title = `${this.flavourName} ${document.title}`;

    this.checkForOtherInstances();
    window.addEventListener("storage", this.localStorageEventListener);

    this.reloadDataInterval = setInterval(() => {
      this.preloadImportantData();
    }, THIRTY_MINUTES);
  },
  methods: {
    ...mapActions("operators", ["getOperator", "logout"]),
    ...mapActions("clientConfigs", ["loadConfigByCentralAndName"]),
    ...mapActions("app", ["reloadWgRequirements"]),
    preloadImportantData() {
      this.preloadStreets();
      this.preloadConfigs();
      this.preloadRequirements();
    },
    async preloadStreets() {
      for (const wg of this.callCenter?.wgs ?? []) {
        await getStreetsForStreetInput(wg.id);
      }
    },
    async preloadConfigs() {
      for (const wg of this.callCenter?.wgs ?? []) {
        for (const central of wg.centrals ?? []) {
          if (central.id > 0) {
            await this.loadConfigByCentralAndName({
              workgroupId: wg.id,
              centralId: central.id,
              name: "generalSettings",
            });
          }
        }
      }
    },
    async preloadRequirements() {
      for (const wg of this.callCenter?.wgs ?? []) {
        await this.reloadWgRequirements(wg.id);
      }
    },
    isUpdate() {
      return !!window.localStorage.getItem("updated");
    },
    checkForOtherInstances() {
      localStorage.openapp = Date.now();
    },
    async checkOperator() {
      const id = localStorage.getItem("opId");
      if (id && this.callCenter?.id) {
        const data = await this.getOperator({
          ccenterId: this.callCenter?.id,
          opId: id,
        });
        if (data.deleted) {
          await this.logout();
          setTimeout(() => {
            this.$router.push({ name: "login" });
          }, 500);
        }
      }
    },
    localStorageEventListener(event) {
      if (event.key == "openapp") {
        localStorage.app_available = Date.now();
      }
      if (event.key == "app_available") {
        this.otherWindow = true;
      }
    },
  },
  watch: {
    ["callCenter.id"]: {
      handler() {
        this.preloadImportantData();
      },
      deep: true,
    },
  },
  computed: {
    ...mapGetters("operators", ["token"]),
    ...mapGetters("app", ["callCenter"]),
    isDevelopment() {
      return window.location.host.includes("local");
    },
    blockMultiWindow() {
      return [SANTANDER_WG, JOVELLANOS_WG, AVILES_WG].includes(this.callCenter?.id);
    },
    loadingText() {
      if (this.isUpdate()) {
        return "Actualizando aplicación de operadora...";
      }
      return "Obteniendo datos...";
    },
  },
};
</script>

<style lang="scss" scoped>
.neositek #loader {
  background: linear-gradient(9deg, rgba(86, 183, 141, 1) 0%, rgba(39, 112, 125, 1) 100%);
}

#loader {
  z-index: 100000;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100vh;
  position: absolute;
  top: 0;
  left: 0;
  background: rgb(48, 65, 86);
  background: linear-gradient(
    9deg,
    rgba(48, 65, 86, 1) 0%,
    rgba(65, 79, 96, 1) 55%,
    rgba(28, 40, 56, 1) 100%
  );
  flex-direction: column;

  &.update {
    background: rgb(64, 150, 20);
    background: linear-gradient(
      9deg,
      rgb(108, 190, 66) 0%,
      rgb(64, 150, 20) 55%,
      rgb(36, 91, 8) 100%
    );
  }

  #logo {
    width: 12em;
    margin-bottom: 40px;
  }

  .text {
    color: white;
    padding: 30px;
  }

  .spinner {
    width: 50px;
    height: 50px;
    --c: radial-gradient(farthest-side, white 92%, #0000);
    background:
      var(--c) 50% 0,
      var(--c) 50% 100%,
      var(--c) 100% 50%,
      var(--c) 0 50%;
    background-size: 12px 12px;
    background-repeat: no-repeat;
    animation: s7 1s infinite;
  }
}

@keyframes s7 {
  to {
    transform: rotate(0.5turn);
  }
}

.testing {
  padding-top: 20px;
}

.testing-global-message {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;

  .message {
    padding: 20px 30px;
    text-align: center;

    strong {
      color: red;
      display: block;
      font-size: 18px;
      margin-bottom: 10px;
    }
  }
}

.testing-message {
  position: fixed;
  z-index: 1010;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 20px;
  line-height: 20px;
  background: #f44;
  color: #fff;
  font-size: 13px;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  a {
    font-weight: bold;
    text-decoration: underline;
  }
}

.progress-container {
  width: 400px;
}
</style>

<style>
body {
  --topbar-height: 50px;
  --color-danger: #f44336;
  --color-primary: #1890ff;
  --color-warning: #ffba00;
}
</style>
